import { LockOpen, ViewListOutlined } from "@material-ui/icons";
import { APIProjectInput } from "api/projects";
import React, { useEffect, useMemo } from "react";
import { Form, FormControl } from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";
import { useAsync } from "react-use";
import styled from "styled-components";
import api from "../../api";
import { APICountry } from "../../api/types";
import { APIUserListSearchParams } from "../../api/users";
import FormLabel from "../../components/FormLabel";
import ReactSelect from "../../components/ReactSelect";
import Project from "../../store/models/Project";

const NameInput = styled(FormControl)`
  font-size: 0.875rem;
  line-height: 0.875rem;
  height: 2.375rem;

  &:focus {
    color: #495057;
    background-color: #ffffff;
    border-color: #74b468;
    outline: #74b468 auto 1px;
    box-shadow: none;
  }
`;

interface Props {
  instance: Project;
  editable?: boolean;
}

interface Inputs extends APIProjectInput {
  additionalCountries: APICountry[];
}

const SettingsForm: React.FC<Props> = ({ instance, editable }) => {
  const {
    control,
    formState: { errors },
    reset,
  } = useFormContext<Inputs>();

  const choicesResults = useAsync(async () => {
    const params: APIUserListSearchParams = {
      is_active: true,
    };

    return api.users.list(undefined, undefined, params);
  });

  useEffect(() => {
    const addtlCountries = instance.additionalCountries.map((item) => ({
      id: item.id,
      name: item.name,
      cluster: item.cluster,
    }));
    reset({
      name: instance.name,
      type: instance.type,
      opsInputUsers: instance.opsInputUsers,
      rmPriceUsers: instance.rmPriceUsers,
      projectOutputUsers: instance.projectOutputUsers,
      adminUsers: instance.adminUsers,
      additionalCountries: addtlCountries,
    });
  }, [instance, reset]);

  const options = useMemo(() => {
    const { loading, value: response } = choicesResults;
    if (!loading && response) {
      if (response.results.length > 0) {
        return response.results.map((item) => {
          return {
            value: item.id,
            label: item.name || `<${item.email}>`,
            avatar: item.photo || "",
          };
        });
      }
    }
    return [];
  }, [choicesResults]);

  const projectTypeOptions = [
    { value: "costing", label: "Costing Exercise" },
    { value: "project", label: "Project" },
    { value: "tender", label: "Tender" },
  ];

  return (
    <>
      <Form.Group>
        <FormLabel>
          <ViewListOutlined />
          Project Name
        </FormLabel>
        <Controller
          name="name"
          control={control}
          defaultValue={instance.name}
          rules={{ required: true }}
          render={({ field }) => (
            <NameInput
              type="text"
              disabled={!editable}
              {...field}
              isInvalid={errors.name}
              onChange={(name: string) => field.onChange(name)}
            />
          )}
        />
        {errors.name && (
          <Form.Control.Feedback type="invalid">
            {(errors.name.type as unknown as string) === "required"
              ? "This field may not be blank."
              : errors.name.message}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      <Form.Group>
        <FormLabel>
          <LockOpen />
          Project Type
        </FormLabel>
        <Controller
          name="type"
          control={control}
          defaultValue={instance.name}
          rules={{ required: true }}
          render={({ field }) => (
            <ReactSelect
              defaultValue={instance.type}
              disabled={!editable}
              name="type"
              options={projectTypeOptions}
              placeholder="Select Project Type"
              noOptionsMessage="-"
              hideSelectedOptions={false}
            />
          )}
        />
      </Form.Group>
      <Form.Group>
        <FormLabel>
          <LockOpen />
          ATP/GSC Ops Input
        </FormLabel>
        <ReactSelect
          defaultValue={instance.opsInputUsers}
          disabled={!editable}
          name="opsInputUsers"
          options={options}
          placeholder="Select users"
          noOptionsMessage="No Users"
          isMulti
        />
      </Form.Group>
      <Form.Group>
        <FormLabel>
          <LockOpen />
          Procurement RM Price Input
        </FormLabel>
        <ReactSelect
          defaultValue={instance.rmPriceUsers}
          disabled={!editable}
          name="rmPriceUsers"
          options={options}
          placeholder="Select users"
          noOptionsMessage="No Users"
          isMulti
        />
      </Form.Group>
      <Form.Group>
        <FormLabel>
          <LockOpen />
          Project Output Recipient
        </FormLabel>
        <ReactSelect
          defaultValue={instance.projectOutputUsers}
          disabled={!editable}
          name="projectOutputUsers"
          options={options}
          placeholder="Select users"
          noOptionsMessage="No Users"
          isMulti
        />
      </Form.Group>
      <Form.Group>
        <FormLabel>
          <LockOpen />
          Admin (Project Owner)
        </FormLabel>
        <ReactSelect
          defaultValue={instance.adminUsers}
          disabled={!editable}
          name="adminUsers"
          options={options}
          placeholder="Select users"
          noOptionsMessage="No Users"
          isMulti
        />
      </Form.Group>
    </>
  );
};

export default SettingsForm;